/**
 * Cross-App Data Access Permissions Manager
 *
 * Provides configuration and persistence for granular cross-app storage sharing,
 * allowing users to inspect and toggle permissions for apps declaring
 * `manifest.permissions.crossAppStorage`.
 */

import { useState, useEffect, useCallback } from 'react';

export const CROSS_APP_PERMISSIONS_EVENT = 'yada:permissions:cross-app-changed';

export type CrossAppPolicy = 'connected' | 'isolated';

export type CrossAppPermissionOverrides = Record<string, Record<string, boolean>>;

/**
 * Human-friendly descriptions for the cross-app storage bridges that remain.
 * Bridges are a temporary, read-only exception to the runtime constitution's
 * cross-app rule: never add a target here; remove one when its caller moves to
 * the owner's query handlers.
 */
export const KNOWN_CROSS_APP_BRIDGES: Record<string, Record<string, { purpose: string }>> = {
  '@yada/app-home': {
    '@yada/app-calendar': { purpose: 'Display upcoming events in Home timeline' },
    '@yada/app-chat': { purpose: 'Show recent messages and unread indicators' },
    '@yada/app-contacts': { purpose: 'Quick search and recent contact interactions' },
    '@yada/app-docs': { purpose: 'Show pinned and recently edited notes' },
    '@yada/app-finance': { purpose: 'Display financial summary and upcoming invoices' },
    '@yada/app-habits': { purpose: 'Show daily habit streaks and quick check-ins' },
    '@yada/app-meal-planner': { purpose: 'Display today’s planned meals and recipes' },
    '@yada/app-shopping-list': { purpose: 'Quick access to active shopping checklists' },
    '@yada/app-todo': { purpose: 'Show urgent and pending tasks' },
    '@yada/app-training': { purpose: 'Display recent workout logs and activity' },
  },
  '@yada/app-meal-planner': {
    '@yada/app-calendar': { purpose: 'Schedule meal plans as calendar events' },
  },
  '@yada/app-contacts': {
    '@yada/app-home': { purpose: 'Access home profile references' },
    '@yada/app-finance': { purpose: 'Associate invoices and payments with contacts' },
    '@yada/app-calendar': { purpose: 'Find shared meetings and appointments' },
    '@yada/app-chat': { purpose: 'Direct messaging integration for contacts' },
  },
  '@yada/app-groups': {
    '@yada/app-chat': { purpose: 'Group chat channels and messaging' },
  },
  '@yada/app-events': {
    '@yada/app-calendar': { purpose: 'Import social events created before Events existed' },
  },
};

export function getCrossAppPermissionsStorageKey(profileId?: string): string {
  const activeProfile = profileId || (typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:activeProfileId') : null);
  return activeProfile ? `yada:permissions:cross-app:${activeProfile}` : 'yada:permissions:cross-app';
}

export function getCrossAppPolicyStorageKey(profileId?: string): string {
  const activeProfile = profileId || (typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:activeProfileId') : null);
  return activeProfile ? `yada:permissions:cross-app-policy:${activeProfile}` : 'yada:permissions:cross-app-policy';
}

/**
 * Retrieve the current global cross-app policy ('connected' vs 'isolated').
 * Default is 'connected'.
 */
export function getCrossAppPolicy(profileId?: string): CrossAppPolicy {
  if (typeof localStorage === 'undefined') return 'connected';
  const raw = localStorage.getItem(getCrossAppPolicyStorageKey(profileId));
  if (raw === 'isolated') return 'isolated';
  return 'connected';
}

/**
 * Set the global cross-app policy.
 */
export function setCrossAppPolicy(policy: CrossAppPolicy, profileId?: string): void {
  if (typeof localStorage === 'undefined') return;
  const key = getCrossAppPolicyStorageKey(profileId);
  localStorage.setItem(key, policy);
  if (typeof window !== 'undefined') {
    if (typeof CustomEvent !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CROSS_APP_PERMISSIONS_EVENT, { detail: { policy } }));
    } else {
      window.dispatchEvent({ type: CROSS_APP_PERMISSIONS_EVENT, detail: { policy } } as any);
    }
  }
}

/**
 * Retrieve current user permission overrides.
 */
export function getCrossAppPermissionOverrides(profileId?: string): CrossAppPermissionOverrides {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(getCrossAppPermissionsStorageKey(profileId));
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

/**
 * Determine whether a caller app is allowed to access target app data.
 */
export function isCrossAppAccessAllowed(callerAppId: string, targetAppId: string, profileId?: string): boolean {
  if (callerAppId === targetAppId) return true;
  const overrides = getCrossAppPermissionOverrides(profileId);
  const callerGrants = overrides[callerAppId];
  if (callerGrants && typeof callerGrants[targetAppId] === 'boolean') {
    return callerGrants[targetAppId];
  }
  // If no explicit override, follow the global policy
  const policy = getCrossAppPolicy(profileId);
  return policy === 'connected';
}

/**
 * Update permission grant for callerAppId -> targetAppId.
 */
export function setCrossAppPermission(
  callerAppId: string,
  targetAppId: string,
  allowed: boolean,
  profileId?: string
): void {
  if (typeof localStorage === 'undefined') return;
  const key = getCrossAppPermissionsStorageKey(profileId);
  const current = getCrossAppPermissionOverrides(profileId);
  const updated: CrossAppPermissionOverrides = {
    ...current,
    [callerAppId]: {
      ...(current[callerAppId] || {}),
      [targetAppId]: allowed,
    },
  };
  localStorage.setItem(key, JSON.stringify(updated));
  if (typeof window !== 'undefined') {
    if (typeof CustomEvent !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CROSS_APP_PERMISSIONS_EVENT, { detail: { callerAppId, targetAppId, allowed } }));
    } else {
      window.dispatchEvent({ type: CROSS_APP_PERMISSIONS_EVENT, detail: { callerAppId, targetAppId, allowed } } as any);
    }
  }
}

/**
 * Reset all cross-app permission overrides and global policy to default ('connected').
 */
export function resetCrossAppPermissions(profileId?: string): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(getCrossAppPermissionsStorageKey(profileId));
  localStorage.removeItem(getCrossAppPolicyStorageKey(profileId));
  if (typeof window !== 'undefined') {
    if (typeof CustomEvent !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CROSS_APP_PERMISSIONS_EVENT, { detail: { reset: true } }));
    } else {
      window.dispatchEvent({ type: CROSS_APP_PERMISSIONS_EVENT, detail: { reset: true } } as any);
    }
  }
}

/**
 * React hook to observe and update cross-app permissions and global policy.
 */
export function useCrossAppPermissions() {
  const [overrides, setOverrides] = useState<CrossAppPermissionOverrides>(() => getCrossAppPermissionOverrides());
  const [policy, setPolicyState] = useState<CrossAppPolicy>(() => getCrossAppPolicy());

  const refresh = useCallback(() => {
    setOverrides(getCrossAppPermissionOverrides());
    setPolicyState(getCrossAppPolicy());
  }, []);

  useEffect(() => {
    const handleEvent = () => refresh();
    window.addEventListener(CROSS_APP_PERMISSIONS_EVENT, handleEvent);
    window.addEventListener('storage', handleEvent);
    return () => {
      window.removeEventListener(CROSS_APP_PERMISSIONS_EVENT, handleEvent);
      window.removeEventListener('storage', handleEvent);
    };
  }, [refresh]);

  const togglePermission = useCallback((callerAppId: string, targetAppId: string, allowed: boolean) => {
    setCrossAppPermission(callerAppId, targetAppId, allowed);
    refresh();
  }, [refresh]);

  const setPolicy = useCallback((newPolicy: CrossAppPolicy) => {
    setCrossAppPolicy(newPolicy);
    refresh();
  }, [refresh]);

  const resetAll = useCallback(() => {
    resetCrossAppPermissions();
    refresh();
  }, [refresh]);

  return {
    overrides,
    policy,
    setPolicy,
    isAllowed: (callerAppId: string, targetAppId: string) => {
      const callerGrants = overrides[callerAppId];
      if (callerGrants && typeof callerGrants[targetAppId] === 'boolean') {
        return callerGrants[targetAppId];
      }
      return policy === 'connected';
    },
    hasExplicitOverride: (callerAppId: string, targetAppId: string) => {
      return typeof overrides[callerAppId]?.[targetAppId] === 'boolean';
    },
    togglePermission,
    resetAll,
  };
}
